/**
 * Loading shapes for the Integrations overview.
 *
 * A separate file from `overview.tsx` because that one is `lazy()` and this one
 * is not — the skeleton has to be in the eager bundle to be what renders while
 * the chunk arrives.
 *
 * Every block mirrors its real counterpart's **element structure** rather than
 * asserting a height, so the real padding and line boxes set the size and the
 * two cannot disagree. The one exception is the KPI card, which has no inner
 * structure to mirror — `KpiStripSkeleton` owns its measured height.
 *
 * The page header is not here at all: it renders above the boundary and never
 * unmounts, so drawing a placeholder for it would be drawing over something
 * already on screen (P-05).
 */
import { KpiStripSkeleton } from '@/components/skeletons/kpi-strip-skeleton'
import { Button } from '@/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * The overview, with only the data taken out.
 *
 * Used as **both** the route's `pendingComponent` and the section's `Suspense`
 * fallback, so the two states cannot drift apart (P-05 §2).
 */
export const IntegrationsOverviewSkeleton = () => {
	return (
		<div className="flex flex-col gap-4">
			<KpiStripSkeleton count={4} />

			<NeedsAttentionSkeleton />

			<div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
				{/* Titles and footer buttons render for real: they are fixed per
				    position and need no server response, so greying them out would hide
				    something already known (P-05 §1). */}
				<EstateSummaryCardSkeleton
					title="Cloud integrations"
					action="Manage Cloud"
				/>
				<EstateSummaryCardSkeleton
					title="Network Collectors"
					action="Manage Network Collectors"
				/>
			</div>
		</div>
	)
}

/**
 * Three rows — a guess, since the count is only known once both lists land.
 * The all-clear band is deliberately *not* the shape guessed at here: it is
 * the shorter of the two states, so a skeleton drawn to its height would grow
 * the page when the longer one lands, which is the shift the skeleton exists
 * to prevent.
 */
function NeedsAttentionSkeleton() {
	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{/* h-5.5 is `CardTitle`'s own line box — `text-base leading-snug` is
					    22px, not the 24 an `h-6` placeholder would claim. */}
					<div className="flex h-5.5 items-center">
						<Skeleton className="h-4 w-40" />
					</div>
				</CardTitle>
				<CardAction>
					{/* `Pill` is a fixed h-5.5 by design (ADR 0020) — one height, no variants. */}
					<Skeleton className="h-5.5 w-8 rounded-full" />
				</CardAction>
			</CardHeader>

			<CardContent className="flex flex-col">
				{Array.from({ length: 3 }).map((_, index) => (
					<div
						key={index}
						className="flex flex-col gap-3 border-t py-3 first:border-t-0 sm:flex-row sm:items-center"
					>
						{/* Each placeholder sits *inside* the line box of the text it
						    stands in for — an `h-5` name line above an `h-5` reason line,
						    no gap, exactly as the row stacks its two elements. Sizing the
						    placeholders themselves to the line height instead would need
						    a gap to keep them apart, and that gap is 4px of height the
						    real row does not have. */}
						<div className="min-w-0 flex-1">
							<div className="flex h-5 items-center">
								<Skeleton className="h-4 w-48" />
							</div>
							<div className="flex h-5 items-center">
								<Skeleton className="h-4 w-72 max-w-full" />
							</div>
						</div>

						<div className="flex shrink-0 items-center gap-3">
							<Skeleton className="h-5.5 w-28 rounded-full" />
							<Skeleton className="h-5.5 w-24 rounded-full" />
							<Skeleton className="h-8 w-20" />
						</div>
					</div>
				))}
			</CardContent>
		</Card>
	)
}

/**
 * One half's card. The title and the footer's button render for real (the button
 * disabled) — both are fixed labels with fixed destinations, so a grey box in
 * their place would be hiding something already known (P-05 §1).
 */
function EstateSummaryCardSkeleton({
	title,
	action,
}: {
	title: string
	action: string
}) {
	return (
		<Card className="justify-between">
			<CardHeader>
				<CardTitle>{title}</CardTitle>
				<CardAction>
					<Skeleton className="h-5.5 w-20 rounded-full" />
				</CardAction>
			</CardHeader>

			<CardContent className="flex flex-col gap-3">
				{/* Line boxes, as above: `text-base` headline (24px) over a `text-sm`
				    detail (20px), stacked with no gap. */}
				<div>
					<div className="flex h-6 items-center">
						<Skeleton className="h-4 w-56" />
					</div>
					<div className="flex h-5 items-center">
						<Skeleton className="h-4 w-40" />
					</div>
				</div>

				<div className="flex flex-col gap-1">
					<Skeleton className="h-1.5 w-full rounded-full" />
					<div className="flex h-5 items-center">
						<Skeleton className="h-4 w-24" />
					</div>
				</div>

				<div className="flex h-5 items-center">
					<Skeleton className="h-4 w-32" />
				</div>
			</CardContent>

			<CardFooter>
				<Button variant="outline" disabled>
					{action}
				</Button>
			</CardFooter>
		</Card>
	)
}
